import React, { useState, useEffect, useRef } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonModal,
    IonContent,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Define question types
type QuestionType = "picture" | "identifyOddOneOut" | "trueFalse" | "fillInTheBlanks" | "multipleChoice";

interface PictureQuestion {
    question: string;
    options: { src: string; answer: boolean }[];
}

interface IdentifyOddOneOutQuestion {
    question: string;
    options: string[];
    correct: string;
}

interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
}

interface TrueFalseQuestion {
    question: string;
    correct: boolean;
}

interface FillInTheBlanksQuestion {
    question: string;
    correct: string;
    options: string[];
}

const TransportVehicleWorksheet: React.FC = () => {
    const [currentQuestion, setCurrentQuestion] = useState<PictureQuestion | IdentifyOddOneOutQuestion | MultipleChoiceQuestion | TrueFalseQuestion | FillInTheBlanksQuestion | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [showResult, setShowResult] = useState<boolean>(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const [questionBank, setQuestionBank] = useState<Record<QuestionType, (PictureQuestion | IdentifyOddOneOutQuestion | MultipleChoiceQuestion | TrueFalseQuestion | FillInTheBlanksQuestion)[]>>({
        picture: [],
        identifyOddOneOut: [],
        multipleChoice: [],
        trueFalse: [],
        fillInTheBlanks: []
    });

    const fetchQuestions = async () => {
        try {
            let multipleChoiceQuestion: MultipleChoiceQuestion[] = [];
            let identifyOddOneOutQuestion: IdentifyOddOneOutQuestion[] = [];
            let trueFalseQuestion: TrueFalseQuestion[] = [];
            let fillInTheBlanksQuestion: FillInTheBlanksQuestion[] = [];
            let pictureQuestionQuestion: PictureQuestion[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {

                const response = await fetch(process.env.REACT_APP_API_URL + 'TransportVehicleTrueFalse.json');
                trueFalseQuestion = await response.json();

                const response1 = await fetch(process.env.REACT_APP_API_URL + 'TransportVehicleMultipleChoice.json');
                multipleChoiceQuestion = await response1.json();

                const response2 = await fetch(process.env.REACT_APP_API_URL + 'TransportVehicleIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = await response2.json();

                const response3 = await fetch(process.env.REACT_APP_API_URL + 'TransportVehicleFillInTheBlanks.json');
                fillInTheBlanksQuestion = await response3.json();

                const response4 = await fetch(process.env.REACT_APP_API_URL + 'TransportVehiclePictureQuestion.json');
                pictureQuestionQuestion = await response4.json();

            } else {
                const module = await import('../../../datas/TransportVehicleTrueFalse.json');
                trueFalseQuestion = module.default;

                const module1 = await import('../../../datas/TransportVehicleMultipleChoice.json');
                multipleChoiceQuestion = module1.default;

                const module2 = await import('../../../datas/TransportVehicleIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = module2.default;

                const module3 = await import('../../../datas/TransportVehicleFillInTheBlanks.json');
                fillInTheBlanksQuestion = module3.default;

                const module4 = await import('../../../datas/TransportVehiclePictureQuestion.json');
                pictureQuestionQuestion = module4.default;
            }
            setQuestionBank({
                picture: pictureQuestionQuestion,
                identifyOddOneOut: identifyOddOneOutQuestion,
                multipleChoice: multipleChoiceQuestion,
                trueFalse: trueFalseQuestion,
                fillInTheBlanks: fillInTheBlanksQuestion,
            });
        } catch (error) {
            console.error("Error loading questions:", error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Modes of Transport for UKG Kids | Learn with Worksheets"; // Set page title
    }, []);

    useEffect(() => {
        if (questionBank.identifyOddOneOut.length || questionBank.multipleChoice.length || questionBank.trueFalse.length || questionBank.fillInTheBlanks.length) {
            getRandomQuestion();
        }
    }, [questionBank]);

    const getRandomQuestion = () => {
        const questionTypes = Object.keys(questionBank) as QuestionType[];
        const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
        const randomQuestion = questionBank[randomType][Math.floor(Math.random() * questionBank[randomType].length)];
        setCurrentQuestion(randomQuestion);
        setSelectedOption(null);
    };

    const handleAnswerSubmit = (option: any) => {
        setSelectedOption(option);
        if (!currentQuestion) return;

        if ("correct" in currentQuestion) {
            if (option === currentQuestion.correct) {
                showNextQuestion();
                setIsCorrect(true);
            }
        } else if ("options" in currentQuestion) {
            // For picture-based questions
            if ((currentQuestion as PictureQuestion).options.find((opt) => opt.src === option)?.answer) {
                showNextQuestion();
                setIsCorrect(true);
            }
        }
    };

    const showNextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {
            setShowImage(false);
            setSelectedOption(null);
            getRandomQuestion();
            setIsCorrect(false)
        }, 5000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content="Explore different types of transport vehicles for UKG students. Learn about land, air, and water transport with fun activities and printable worksheets." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/environmental-science/transport-vehicles" />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Transport and Vehicles</h1>

                                <p>
                                    Transport is how we move from one place to another using different types of vehicles. In UKG, children learn to identify various modes of transport and their uses — an important environmental studies topic that connects learning with the real world.
                                </p>

                                <h2>🚗 Types of Transport</h2>
                                <ul>
                                    <li><strong>Land Transport:</strong> Cars, buses, bicycles, trains, scooters</li>
                                    <li><strong>Air Transport:</strong> Airplanes, helicopters, hot air balloons</li>
                                    <li><strong>Water Transport:</strong> Boats, ships, submarines, ferries</li>
                                </ul>

                                <h2>🎯 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Identify and name different vehicles</li>
                                    <li>Classify vehicles by land, air, and water</li>
                                    <li>Understand the purpose and use of each mode</li>
                                    <li>Match vehicles to their transport type</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/environmental-science/living-non-living-things"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Living & Non-Living Things</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/environmental-science/traffic-rules-safety"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Traffic Rules & Safety</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🏠 Home Learning Ideas</h2>
                                <ul>
                                    <li>Show your child real transport videos or toys</li>
                                    <li>Play sorting games: “Is this land, air or water?”</li>
                                    <li>Make a simple paper collage of transport types</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Transport and Vehicles</h1>

                            <p>
                                Transport is how we move from one place to another using different types of vehicles. In UKG, children learn to identify various modes of transport and their uses — an important environmental studies topic that connects learning with the real world.
                            </p>

                            <h2>🚗 Types of Transport</h2>
                            <ul>
                                <li><strong>Land Transport:</strong> Cars, buses, bicycles, trains, scooters</li>
                                <li><strong>Air Transport:</strong> Airplanes, helicopters, hot air balloons</li>
                                <li><strong>Water Transport:</strong> Boats, ships, submarines, ferries</li>
                            </ul>

                            <h2>🎯 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Identify and name different vehicles</li>
                                <li>Classify vehicles by land, air, and water</li>
                                <li>Understand the purpose and use of each mode</li>
                                <li>Match vehicles to their transport type</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/environmental-science/living-non-living-things"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Living & Non-Living Things</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/environmental-science/traffic-rules-safety"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Traffic Rules & Safety</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🏠 Home Learning Ideas</h2>
                            <ul>
                                <li>Show your child real transport videos or toys</li>
                                <li>Play sorting games: “Is this land, air or water?”</li>
                                <li>Make a simple paper collage of transport types</li>
                            </ul>
                        </article>
                        {currentQuestion ? (
                            <>
                                <h3 className="mb-2">{currentQuestion.question}</h3>

                                {"options" in currentQuestion ? (
                                    // Handle multiple-choice or identify odd one out question
                                    typeof currentQuestion.options[0] != "object" ? (
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                                            {(currentQuestion as IdentifyOddOneOutQuestion | MultipleChoiceQuestion | FillInTheBlanksQuestion).options.map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? isCorrect === true
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}>
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonRadioGroup>
                                    ) : (
                                        // Handle picture question
                                        <div className="row">
                                            {(currentQuestion as PictureQuestion).options.map((option, index) => (
                                                <div
                                                    className={`col-12 col-md-3 mb-5 `}
                                                    key={index}>
                                                    <img
                                                        src={process.env.REACT_APP_API_URL + option.src}
                                                        onClick={() => handleAnswerSubmit(option.src)}
                                                        className={`img-thumbnail ${selectedOption === option.src
                                                            ? isCorrect === true
                                                                ? "correct"
                                                                : "errorImgItems"
                                                            : ""
                                                            }`}
                                                        style={{
                                                            cursor: "pointer",
                                                            height: '150px',
                                                            width: '150px'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )
                                ) : (
                                    // Handle True/False question
                                    <>
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value!)}>
                                            <IonItem
                                                className={`grid-item ${selectedOption === true
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={true} />
                                                <IonLabel>True</IonLabel>
                                            </IonItem>
                                            <IonItem
                                                className={`grid-item ${selectedOption === false
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={false} />
                                                <IonLabel>False</IonLabel>
                                            </IonItem>
                                        </IonRadioGroup>
                                    </>
                                )}
                            </>
                        ) : (
                            <p>Loading question...</p>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default TransportVehicleWorksheet;